import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { KPI_LABELS, DEAL_TYPES } from "@/lib/dealSpecs";
import { fmtBy } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, RadialBarChart, RadialBar, ReferenceLine } from "recharts";
import { Download, Sparkles, HardHat } from "lucide-react";
import { NewProjectModal } from "@/pages/app/projects/ProjectsList";

const NAVY = "#0F2747";
const ICE = "#DCEEFF";
const SLATE = "#2A2E33";
const TABS = ["Overview", "Scenarios", "Sensitivity", "Score", "AI Review", "Export"];

export default function DealDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [deal, setDeal] = useState(null);
    const [tab, setTab] = useState("Overview");
    const [sensitivity, setSensitivity] = useState(null);
    const [scenarioName, setScenarioName] = useState("");
    const [aiReview, setAiReview] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [showConvert, setShowConvert] = useState(false);

    const load = async () => { const { data } = await api.get(`/deals/${id}`); setDeal(data); };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const kpis = deal ? (KPI_LABELS[deal.deal_type] || []) : [];
    const typeLabel = deal ? DEAL_TYPES.find((d) => d.key === deal.deal_type)?.label || deal.deal_type : "";

    const updateStatus = async (status) => {
        try { await api.patch(`/deals/${id}`, { status }); toast.success(`Marked ${status.replace(/_/g, " ")}`); load(); }
        catch { toast.error("Failed"); }
    };

    const runSensitivity = async () => {
        try { const { data } = await api.get(`/deals/${id}/sensitivity`); setSensitivity(data); }
        catch { toast.error("Failed"); }
    };

    const addScenario = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/deals/${id}/scenarios`, { name: scenarioName, inputs: {} });
            toast.success("Scenario added"); setScenarioName(""); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    const computeScore = async () => {
        try {
            const { data } = await api.post(`/deals/${id}/score`, { ratings: {}, disqualifiers: {} });
            toast.success(`Score ${data.score} · ${data.grade}`); load();
        } catch { toast.error("Failed"); }
    };

    const askAI = async (save = false) => {
        setAiLoading(true);
        try {
            const { data } = await api.post(`/deals/${id}/ai-review?save=${save}`);
            setAiReview(data);
            if (save) { toast.success("AI review saved"); load(); }
        } catch (err) { toast.error(err?.response?.data?.detail || "AI failed"); }
        finally { setAiLoading(false); }
    };

    if (!deal) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

    const uses = deal.results?.sources_uses?.uses ? Object.entries(deal.results.sources_uses.uses).map(([k, v]) => ({ name: k, value: v })) : [];
    const sources = deal.results?.sources_uses?.sources ? Object.entries(deal.results.sources_uses.sources).map(([k, v]) => ({ name: k, value: v })) : [];

    return (
        <div className="p-6 md:p-10" data-testid="page-deal-detail">
            <Link to="/app/deals" className="text-xs text-muted-foreground hover:opacity-70">← Back to deals</Link>
            <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="chip chip-navy">{typeLabel}</div>
                    <h1 className="mt-3 font-display text-4xl tracking-tight leading-none italic">{deal.name}</h1>
                    <div className="mt-2 text-xs text-muted-foreground capitalize">Status: {deal.status.replace(/_/g, " ")}</div>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    {deal.status === "approved" && !deal.project_id && (
                        <button onClick={() => setShowConvert(true)} className="inline-flex items-center gap-2 text-xs px-3 py-2 bg-navy text-arch-white hover:opacity-90" data-testid="deal-convert-to-project">
                            <HardHat size={12} /> Convert to Project
                        </button>
                    )}
                    {deal.project_id && (
                        <Link to={`/app/projects/${deal.project_id}`} className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-navy text-navy hover:bg-navy/10" data-testid="deal-open-project">
                            <HardHat size={12} /> Open Project
                        </Link>
                    )}
                    <button onClick={() => updateStatus("under_review")} className="text-xs px-3 py-2 border border-border hover:bg-secondary" data-testid="deal-review">Review</button>
                    <button onClick={() => updateStatus("approved")} className="text-xs px-3 py-2 bg-navy text-arch-white hover:opacity-90" data-testid="deal-approve">Approve</button>
                    <button onClick={() => updateStatus("rejected")} className="text-xs px-3 py-2 border border-destructive text-destructive hover:bg-destructive/10" data-testid="deal-reject">Reject</button>
                </div>
            </div>
            {showConvert && (
                <NewProjectModal
                    deal={deal}
                    onClose={() => setShowConvert(false)}
                    onCreated={(projectId) => { setShowConvert(false); nav(`/app/projects/${projectId}`); }}
                />
            )}

            <div className="mt-6 flex flex-wrap gap-1 border-b border-border">
                {TABS.map((t) => (
                    <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm border-b-2 -mb-px ${tab === t ? "border-navy text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`deal-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</button>
                ))}
            </div>

            <div className="mt-8">
                {tab === "Overview" && (
                    <div className="space-y-8">
                        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="deal-kpis">
                            {kpis.map((k) => (
                                <div key={k.key} className="border border-border p-4" data-testid={`deal-kpi-${k.key}`}>
                                    <div className="overline">{k.label}</div>
                                    <div className="mt-1 font-display text-2xl tracking-tight">{fmtBy(k.fmt, deal.results?.[k.key])}</div>
                                </div>
                            ))}
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            {uses.length > 0 && (
                                <div className="border border-border p-6">
                                    <div className="overline mb-3">Uses of funds</div>
                                    <ResponsiveContainer width="100%" height={240}>
                                        <BarChart data={uses}><XAxis dataKey="name" fontSize={10} /><YAxis fontSize={10} /><Tooltip /><Bar dataKey="value" fill={NAVY} /></BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {sources.length > 0 && (
                                <div className="border border-border p-6">
                                    <div className="overline mb-3">Capital stack</div>
                                    <ResponsiveContainer width="100%" height={240}>
                                        <PieChart><Pie data={sources} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90}>{sources.map((_, i) => <Cell key={i} fill={i === 0 ? SLATE : NAVY} />)}</Pie><Tooltip /></PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {deal.results?.projection_5yr && (
                                <div className="border border-border p-6 md:col-span-2">
                                    <div className="overline mb-3">5-year projection</div>
                                    <ResponsiveContainer width="100%" height={240}>
                                        <LineChart data={deal.results.projection_5yr}><XAxis dataKey="year" fontSize={10} /><YAxis fontSize={10} /><Tooltip /><Line type="monotone" dataKey="cash_flow" stroke={NAVY} strokeWidth={2} dot={false} /><Line type="monotone" dataKey="noi" stroke={ICE} strokeWidth={1.5} dot={false} /></LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "Scenarios" && (
                    <div className="space-y-6">
                        <form onSubmit={addScenario} className="border border-border p-4 space-y-3" data-testid="scenario-form">
                            <div className="overline">Add scenario</div>
                            <div className="grid grid-cols-2 gap-3">
                                <input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} placeholder="Conservative / Optimistic / Custom" className="border border-border bg-background px-3 py-2 text-sm" required data-testid="scenario-name" />
                                <button type="submit" className="bg-foreground text-background px-4 py-2 text-sm hover:opacity-90" data-testid="scenario-add">Add scenario (uses current inputs)</button>
                            </div>
                        </form>
                        <div className="border border-border" data-testid="scenarios-table">
                            <div className="grid px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: `1fr repeat(${kpis.slice(0, 3).length}, 1fr)` }}>
                                <div>Name</div>{kpis.slice(0, 3).map((k) => <div key={k.key}>{k.label}</div>)}
                            </div>
                            <div className="grid px-4 py-3 border-b border-border text-sm bg-secondary/40" style={{ gridTemplateColumns: `1fr repeat(${kpis.slice(0, 3).length}, 1fr)` }}>
                                <div className="font-medium">Base</div>{kpis.slice(0, 3).map((k) => <div key={k.key}>{fmtBy(k.fmt, deal.results?.[k.key])}</div>)}
                            </div>
                            {(deal.scenarios || []).map((s) => (
                                <div key={s.id} className="grid px-4 py-3 border-b border-border last:border-b-0 text-sm" style={{ gridTemplateColumns: `1fr repeat(${kpis.slice(0, 3).length}, 1fr)` }} data-testid={`scenario-row-${s.id}`}>
                                    <div className="font-medium">{s.name}</div>{kpis.slice(0, 3).map((k) => <div key={k.key}>{fmtBy(k.fmt, s.results?.[k.key])}</div>)}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "Sensitivity" && (
                    <SensitivityTab sensitivity={sensitivity} onRun={runSensitivity} />
                )}

                {tab === "Score" && (
                    <div className="space-y-6" data-testid="score-panel">
                        <button onClick={computeScore} className="bg-navy text-arch-white px-4 py-2 text-sm hover:opacity-90" data-testid="compute-score">Compute deal score</button>
                        {deal.deal_score && (
                            <div className="border border-border p-6">
                                <div className="grid md:grid-cols-3 gap-6">
                                    <div>
                                        <div className="overline">Score</div>
                                        <div className="mt-2 font-display text-6xl tracking-tight text-navy">{deal.deal_score.score}</div>
                                        <div className="mt-1 text-sm">Grade {deal.deal_score.grade}</div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <ResponsiveContainer width="100%" height={200}>
                                            <RadialBarChart innerRadius="30%" outerRadius="100%" data={[{ name: "score", value: deal.deal_score.score, fill: NAVY }]} startAngle={225} endAngle={-45}>
                                                <RadialBar dataKey="value" />
                                                <Tooltip />
                                            </RadialBarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                                {deal.deal_score.disqualifier_hit && <div className="mt-4 text-sm text-destructive">Disqualifier flag active — score capped at 40.</div>}
                            </div>
                        )}
                    </div>
                )}

                {tab === "AI Review" && (
                    <div className="space-y-4" data-testid="ai-review-panel">
                        <div className="flex items-center gap-3">
                            <button onClick={() => askAI(false)} disabled={aiLoading} className="bg-navy text-arch-white px-4 py-2 text-sm hover:opacity-90 inline-flex items-center gap-2 disabled:opacity-50" data-testid="ai-review-preview">
                                <Sparkles size={14} /> {aiLoading ? "Thinking…" : "Preview AI review"}
                            </button>
                            {aiReview && !aiReview.saved && <button onClick={() => askAI(true)} className="border border-border px-4 py-2 text-sm" data-testid="ai-review-save">Save to deal</button>}
                        </div>
                        {aiReview && (
                            <div className="border border-border p-6">
                                <div className="chip chip-ai">AI narrative · human confirmation required</div>
                                <div className="mt-4 whitespace-pre-wrap text-sm">{aiReview.narrative}</div>
                                <div className="mt-4 text-xs text-muted-foreground">Model: {aiReview.model} · {aiReview.generated_at}</div>
                            </div>
                        )}
                        {deal.ai_review?.saved && !aiReview && (
                            <div className="border border-border p-6">
                                <div className="chip chip-ai">Saved AI review</div>
                                <div className="mt-4 whitespace-pre-wrap text-sm">{deal.ai_review.narrative}</div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "Export" && (
                    <div className="space-y-3" data-testid="export-panel">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/deals/${id}/export.pdf`} download className="inline-flex items-center gap-2 bg-navy text-arch-white px-4 py-2 text-sm hover:opacity-90" data-testid="export-pdf"><Download size={14} /> Export PDF</a>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/deals/${id}/export.xlsx`} download className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-secondary ml-2" data-testid="export-xlsx"><Download size={14} /> Export XLSX</a>
                        <div className="mt-4 text-xs text-muted-foreground italic">Preliminary underwriting only. Not a substitute for professional legal, financial, engineering, appraisal, title, or construction review.</div>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Sensitivity tab: tornado chart + heatmap grid.
 * Backend returns { deltas: [-0.2,-0.1,0.1,0.2], kpis: [...], matrix: [{variable, cells:[{delta, kpis:{...}}]}] , base_kpis: {...} }
 */
function SensitivityTab({ sensitivity, onRun }) {
    const primaryKpi = sensitivity?.kpis?.[0];
    // Tornado: sort variables by |max KPI change - min KPI change|
    const tornado = React.useMemo(() => {
        if (!sensitivity || !primaryKpi) return [];
        return sensitivity.matrix.map((row) => {
            const values = row.cells.map((c) => Number(c.kpis?.[primaryKpi]) || 0);
            const base = Number(sensitivity.base_kpis?.[primaryKpi]) || 0;
            const minv = Math.min(...values);
            const maxv = Math.max(...values);
            return { variable: row.variable, low: minv - base, high: maxv - base, base, range: Math.abs(maxv - minv) };
        }).sort((a, b) => b.range - a.range);
    }, [sensitivity, primaryKpi]);

    const kpiFormat = (key) => (key || "").includes("pct") ? "pct" : (key || "").toLowerCase().includes("ratio") || key === "dscr" ? "ratio" : "money";
    const heatColor = (val, base) => {
        if (base === 0 || val === undefined || val === null) return "rgba(220,238,255,0.08)";
        const diff = (val - base) / (Math.abs(base) || 1);
        const clamped = Math.max(-0.5, Math.min(0.5, diff));
        // Ice blue for positive, muted navy for negative
        if (clamped >= 0) return `rgba(220,238,255,${0.10 + clamped * 0.9})`;
        return `rgba(15,39,71,${0.15 + Math.abs(clamped) * 0.7})`;
    };

    return (
        <div className="space-y-6" data-testid="sensitivity-panel">
            <button onClick={onRun} className="bg-navy text-arch-white px-4 py-2 text-sm hover:opacity-90" data-testid="run-sensitivity">Run sensitivity ±10/±20%</button>

            {sensitivity && primaryKpi && (
                <>
                    {/* Tornado */}
                    <div className="border border-border p-6" data-testid="sensitivity-tornado">
                        <div className="overline mb-4">Tornado — variables sorted by impact on {primaryKpi.replace(/_/g, " ")}</div>
                        <ResponsiveContainer width="100%" height={Math.max(160, tornado.length * 40)}>
                            <BarChart layout="vertical" data={tornado} stackOffset="sign" margin={{ left: 40 }}>
                                <XAxis type="number" fontSize={10} />
                                <YAxis type="category" dataKey="variable" fontSize={11} width={150} tickFormatter={(v) => v.replace(/_/g, " ")} />
                                <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                                <ReferenceLine x={0} stroke="#111315" />
                                <Bar dataKey="low" fill={NAVY} />
                                <Bar dataKey="high" fill={ICE} />
                            </BarChart>
                        </ResponsiveContainer>
                        <div className="mt-3 flex gap-4 text-[11px] text-muted-foreground">
                            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 inline-block" style={{ background: NAVY }} /> Downside</span>
                            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 inline-block" style={{ background: ICE }} /> Upside</span>
                        </div>
                    </div>

                    {/* Heatmap */}
                    <div className="border border-border p-6" data-testid="sensitivity-heatmap">
                        <div className="overline mb-4">Heatmap — {primaryKpi.replace(/_/g, " ")}</div>
                        <div className="overflow-x-auto">
                            <div className="grid" style={{ gridTemplateColumns: `160px repeat(${sensitivity.deltas.length}, minmax(90px, 1fr))` }}>
                                <div />
                                {sensitivity.deltas.map((d) => (
                                    <div key={d} className="text-xs text-center py-2 font-medium">{d > 0 ? `+${Math.round(d * 100)}%` : `${Math.round(d * 100)}%`}</div>
                                ))}
                                {sensitivity.matrix.map((row) => {
                                    const base = Number(sensitivity.base_kpis?.[primaryKpi]) || 0;
                                    return (
                                        <React.Fragment key={row.variable}>
                                            <div className="text-xs py-3 pr-2 font-medium capitalize">{row.variable.replace(/_/g, " ")}</div>
                                            {row.cells.map((c, i) => {
                                                const v = Number(c.kpis?.[primaryKpi]);
                                                return (
                                                    <div key={i} className="border border-border/50 text-xs p-2 text-center" style={{ backgroundColor: heatColor(v, base) }} data-testid={`heat-${row.variable}-${i}`}>
                                                        {isFinite(v) ? fmtBy(kpiFormat(primaryKpi), v) : "—"}
                                                    </div>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="mt-3 text-[11px] text-muted-foreground">Ice-blue cells are upside vs. base; navy-toned cells are downside.</div>
                    </div>
                </>
            )}
        </div>
    );
}
